import { useEffect, useMemo, useState } from 'react'
import { Skeleton } from '@wiggui/ds/Skeleton'
import { composeClasses } from '@wiggot/utils'

interface CircleProgressBarProps {
  value: number
  max: number
  emptyBarColor?: string
  activeBarColor?: string
  className?: string
  isLoading?: boolean
  isPercentage?: boolean
}

const RADIUS = 20
const STROKE_WIDTH = 4
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export const CircleProgressBar = ({
  value,
  max,
  emptyBarColor = 'text-surface-gray-20',
  activeBarColor = 'text-notif-blue',
  className,
  isLoading,
  isPercentage
}: CircleProgressBarProps) => {
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const progress = useMemo(() => {
    if (isPercentage) {
      return (value / 100) * CIRCUMFERENCE
    }

    return (value / max) * CIRCUMFERENCE
  }, [value, max, isPercentage])

  useEffect(() => {
    setAnimatedProgress(progress)
  }, [progress])

  return (
    <div
      className={composeClasses(
        className,
        'relative flex items-center justify-center w-12 h-12'
      )}
    >
      <svg className="transform -rotate-90" width="48" height="48">
        <circle
          className={composeClasses(
            emptyBarColor,
            isLoading && 'animate-pulse'
          )}
          strokeWidth={STROKE_WIDTH}
          stroke="currentColor"
          fill="transparent"
          r={RADIUS}
          cx="24"
          cy="24"
        />
        <circle
          className={composeClasses(
            isLoading && 'animate-pulse',
            isLoading ? emptyBarColor : activeBarColor
          )}
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE - animatedProgress}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={RADIUS}
          cx="24"
          cy="24"
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      <div className="absolute flex items-center justify-center">
        {isLoading && (
          <>
            <Skeleton width="w-[12px]" height="h-[20px]" />
            <span className="text-body-m text-secondary">/</span>
            <Skeleton width="w-[12px]" height="h-[20px]" />
          </>
        )}
        {!isLoading && !isPercentage && (
          <span className="text-body-m text-secondary">{`${value}/${max}`}</span>
        )}
        {!isLoading && isPercentage && (
          <span className="text-body-xs text-primary">{`${value}%`}</span>
        )}
      </div>
    </div>
  )
}

export default CircleProgressBar
